import { getApiUrl } from '@/utils/apiConfig';
import { create } from 'zustand';

export interface PresentationData {
  description: string;
  statut: string;
  activites: string;
  contribution: string;
  projets: string;
  website: string;
  facebook: string;
  twitter: string;
  instagram: string;
  youtube: string;
  logoUrl: string;
  image1Url: string;
  image2Url: string;
}

export interface PresentationRecord {
  entityId: string;
  role: string;
  data: PresentationData | null;
  updatedAt: string;
}

interface PresentationsStoreState {
  presentations: PresentationRecord[];
  fetchPresentations: () => Promise<void>;
  savePresentation: (entityId: string, role: string, data: PresentationData) => Promise<boolean>;
}

export const usePresentationsStore = create<PresentationsStoreState>((set, get) => ({
  presentations: [],
  
  fetchPresentations: async () => {
    try {
      const res = await fetch(`${getApiUrl()}/presentations.php?t=${Date.now()}`);
      if (!res.ok) throw new Error("API Error fetching presentations");
      const data = await res.json();
      set({ presentations: Array.isArray(data) ? data : [] });
    } catch (e) {
      console.error("Failed to fetch presentations", e);
    }
  },

  savePresentation: async (entityId, role, data): Promise<boolean> => {
    try {
      const token = localStorage.getItem('kaynin_auth_token');
      
      const payload = {
        action: 'save',
        entityId,
        role,
        data
      };
      
      // Base64 encode the payload to bypass aggressive WAF rules on Hostinger
      const encodedPayload = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));

      const res = await fetch(`${getApiUrl()}/presentations.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'text/plain',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: encodedPayload
      });
      
      if (!res.ok) {
        let errText = await res.text();
        try {
          const errJson = JSON.parse(errText);
          if (errJson.error) errText = errJson.error;
        } catch (e) {
          // ignore
        }
        console.error("Server returned error:", res.status, errText);
        alert(`Erreur de sauvegarde: ${res.status} - ${errText}`);
        return false;
      }

      // Verify the response is actually from our API and not a WAF interception
      const responseText = await res.text();
      try {
        const jsonResponse = JSON.parse(responseText);
        if (jsonResponse.error) {
          alert(`Erreur du serveur: ${jsonResponse.error}`);
          return false;
        }
      } catch (e) {
        console.error("WAF interception or invalid response:", responseText);
        alert("La sauvegarde a été bloquée par le pare-feu du serveur (WAF). Veuillez contacter l'administrateur.");
        return false;
      }

      // Update local state if successful
      set(state => {
        const index = state.presentations.findIndex(p => p.entityId === entityId && p.role === role);
        const newRecord: PresentationRecord = {
          entityId,
          role,
          data,
          updatedAt: new Date().toISOString()
        };
        
        if (index >= 0) {
          const newList = [...state.presentations];
          newList[index] = newRecord;
          return { presentations: newList };
        }
        return { presentations: [...state.presentations, newRecord] };
      });
      return true;
    } catch (e) {
      console.error("Failed to save presentation", e);
      alert("Erreur réseau: " + (e as Error).message);
      return false;
    }
  }
}));

/**
 * Retrouve la présentation V2 d'une entité (Parti, ONG, Institution, Média)
 * @param entityId L'identifiant (ex: acronyme du parti, nom de l'ONG)
 * @param role Le rôle de l'entité (ex: PARTY, ONG, INSTITUTION, MEDIA)
 */
export function getPresentationV2ForEntity(entityId: string, role: string): PresentationData | null {
  // Try to find it in the Zustand store (from backend)
  const storePres = usePresentationsStore.getState().presentations.find(
    p => p.entityId === entityId && p.role === role
  );
  
  if (storePres && storePres.data) {
    return storePres.data;
  }
  
  // Fallback to old localStorage method for migration/compatibility
  // (In case some users have local data not yet saved to backend)
  const storedUsersRaw = localStorage.getItem('kaynin_users');
  if (!storedUsersRaw) return null;

  try {
    const users = JSON.parse(storedUsersRaw);
    let matchedUser = null;

    if (role === 'PARTY') {
      matchedUser = users.find((u: any) => u.role === 'PARTY' && u.partyAcronym === entityId);
    } else if (role === 'ONG') {
      matchedUser = users.find((u: any) => u.role === 'ONG' && u.ongName === entityId);
    } else if (role === 'INSTITUTION') {
      matchedUser = users.find((u: any) => u.role === 'INSTITUTION' && u.institutionName === entityId);
    } else if (role === 'MEDIA') {
      matchedUser = users.find((u: any) => u.role === 'MEDIA' && u.channelName === entityId);
    }

    if (matchedUser && matchedUser.id) {
      const presRaw = localStorage.getItem(`kaynin_presentation_v2_${matchedUser.id}`);
      if (presRaw) {
        return JSON.parse(presRaw) as PresentationData;
      }
    }
  } catch (e) {
    console.error("Erreur de lecture des présentations", e);
  }

  return null;
}
